import { useGstWording } from "../plans/useGstWording";
import { pinWords } from "../../api/planFamily";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CashCodeDialog } from "../../components/CashCodeDialog";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { flagEnabled } from "../../config";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import {
  formError,
  getRechargeOptions,
  listPriceWithGST,
  activePlanUntil,
  advanceRechargeWarning,
  planChangeWarning,
  previousPlan,
  rechargeSubscriber,
  startPayOnline,
  type PayOnlineView,
  receiptKindLabel,
  saleReceipts,
  whyDevicesBlock,
  whyNotRechargeable,
  type RechargeMethod,
  type RechargeResult,
  type RechargeStart,
} from "../../api/subscriberRecharge";
import { sendSubscriberReceipt } from "../../api/subscriberReceipts";
import {
  amountMatchesTotal,
  cartAddons,
  isPriceChanged,
  priceChangedTotal,
  rememberedSelection,
  rupees,
  type CartSelection,
} from "../../api/cart";
import { CartAddons, cartQuoteKey, useCartQuote } from "./CartAddons";
import { PlanPicker, usePlanTermPrices } from "./PlanPicker";
import { PayOnlinePanel } from "./PayOnlinePanel";
import { PayOnlineNotice, UpiPayQR, usePayOptions } from "../payments/PayBlock";
import { PAY_ONLINE_OFF_NOTICE } from "../../api/payOptions";

/** Cash and UPI are taken at the counter; "online" is a gateway payment link (owner 2026-10-28). */
type PayMethod = RechargeMethod | "online";

function newRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Recharge one subscriber (owner 2026-10-10): pick a plan, record the cash or
 * UPI payment taken, and every device of the account gets the plan. One
 * receipt; "Send to subscriber" when they ask for it.
 */
export function SubscriberRechargeDialog({
  ns,
  subscriber,
  onClose,
}: {
  ns: string;
  subscriber: InvoicePrefSubscriber | null;
  onClose: () => void;
}) {
  // Owner 2026-10-29: no word of GST for an unregistered or composition operator.
  const { speak: speakGst } = useGstWording(ns);
  const identity = useIdentity();
  const qc = useQueryClient();
  const accountId = subscriber?.accountid ?? "";
  const opts = useQuery({
    queryKey: ["rechargeoptions", ns, accountId],
    enabled: Boolean(identity.token && ns && accountId),
    queryFn: ({ signal }) =>
      getRechargeOptions(identity, ns, accountId, signal),
    retry: false,
  });
  const activeUntil = opts.data
    ? activePlanUntil(opts.data.devices, Date.now())
    : "";
  const advanceWarning = advanceRechargeWarning(activeUntil, Date.now());

  const [groupname, setGroupname] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PayMethod>("cash");
  // Pay online (owner 2026-10-28, behind recharge.payonline): the gateway texts the link (POL-2) unless unticked.
  const [notifySMS, setNotifySMS] = useState(true);
  const [link, setLink] = useState<PayOnlineView | null>(null);
  const online = method === "online";
  const payOptions = usePayOptions(ns, accountId);
  // Greyed, never absent (owner 2026-11-01).
  const canPayOnline =
    flagEnabled("recharge.payonline") &&
    payOptions.data?.online.available === true;
  // While a plan is active the operator asks the subscriber when the new one starts.
  const [start, setStart] = useState<RechargeStart>("after");
  // Owner 2026-10-25: more than 5 days left needs the operator's tick.
  const [advanceOk, setAdvanceOk] = useState(false);
  // Owner 2026-10-28: the previous plan is pre-selected once; choosing another needs the operator's tick.
  const [preselectedFor, setPreselectedFor] = useState("");
  const [changeOk, setChangeOk] = useState(false);
  const [reference, setReference] = useState("");
  // One id per attempt: a retry after a lost answer is answered with the same receipt.
  const [requestid, setRequestid] = useState(newRequestId);
  const [done, setDone] = useState<RechargeResult | null>(null);
  // Subscriber cart v1: add-ons for this term, priced by the server.
  const [selection, setSelection] = useState<CartSelection>({});
  const [seededFor, setSeededFor] = useState("");
  const [quotedTotal, setQuotedTotal] = useState<number | null>(null);
  const [notice, setNotice] = useState("");
  const previous = opts.data
    ? previousPlan(opts.data.devices, opts.data.plans)
    : "";
  if (opts.data && preselectedFor !== accountId) {
    setPreselectedFor(accountId);
    if (!groupname && previous) setGroupname(previous);
  }
  const changeWarning = planChangeWarning(previous, groupname);
  const cart = useCartQuote(
    identity,
    ns,
    "recharge",
    groupname,
    selection,
    accountId,
  );
  const termPrices = usePlanTermPrices(
    identity,
    ns,
    "recharge",
    accountId,
    opts.data?.plans ?? [],
    groupname,
    whyNotRechargeable,
  );
  const quote = cart.data;
  // null: no quote, or a quote with no price row (radaccess then checks no amount).
  const total = quote?.total ?? null;

  // Pre-tick what the subscriber had last time, once per plan chosen (owner O1).
  if (quote && seededFor !== groupname) {
    setSeededFor(groupname);
    setSelection(rememberedSelection(quote, "recharge"));
  }
  // The amount defaults to the server's total and follows it.
  if (total != null && total !== quotedTotal) {
    setQuotedTotal(total);
    setAmount(String(total));
  }

  const [cashOpen, setCashOpen] = useState(false);
  const recharge = useMutation({
    mutationFn: (cashCode: string | undefined) => {
      const addons = cartAddons(selection);
      return rechargeSubscriber(identity, ns, accountId, {
        groupname,
        amount: Number(amount),
        method: method === "online" ? "cash" : method,
        reference: reference.trim(),
        requestid,
        ...(addons.length ? { addons } : {}),
        ...(activeUntil ? { start } : {}),
        ...(cashCode ? { cash_code: cashCode } : {}),
      });
    },
    onError: async (e) => {
      if (!isPriceChanged(e)) return;
      // Nothing was recorded: say why, ask the server again, and use a fresh attempt id.
      const now = priceChangedTotal(e);
      setNotice(
        `${e.message}${now != null ? ` — the total is now ${rupees(now)}` : ""}. The total below is the new price.`,
      );
      setRequestid(newRequestId());
      await qc.invalidateQueries({
        queryKey: cartQuoteKey(ns, "recharge", accountId),
      });
    },
    onSuccess: async (r) => {
      setNotice("");
      setDone(r);
      await qc.invalidateQueries({
        queryKey: ["subscriberreceipts", ns, accountId],
      });
      await qc.invalidateQueries({
        queryKey: ["rechargeoptions", ns, accountId],
      });
    },
  });
  const payOnline = useMutation({
    mutationFn: () => {
      const addons = cartAddons(selection);
      return startPayOnline(identity, ns, accountId, {
        groupname,
        amount: Number(amount),
        requestid,
        notify_sms: notifySMS,
        ...(addons.length ? { addons } : {}),
        ...(activeUntil ? { start } : {}),
      });
    },
    onSuccess: (v) => setLink(v),
  });
  // Every receipt of the sale goes to the subscriber, one after the other.
  const send = useMutation({
    mutationFn: async (receiptnos: string[]) => {
      let last: Awaited<ReturnType<typeof sendSubscriberReceipt>> | undefined;
      for (const no of receiptnos)
        last = await sendSubscriberReceipt(identity, ns, accountId, no);
      if (!last) throw new Error("no receipt to send");
      return last;
    },
  });

  const close = () => {
    setGroupname("");
    setAmount("");
    setMethod("cash");
    setNotifySMS(true);
    setLink(null);
    payOnline.reset();
    setStart("after");
    setAdvanceOk(false);
    setPreselectedFor("");
    setChangeOk(false);
    setReference("");
    setRequestid(newRequestId());
    setDone(null);
    setSelection({});
    setSeededFor("");
    setQuotedTotal(null);
    setNotice("");
    recharge.reset();
    send.reset();
    onClose();
  };

  const data = opts.data;
  const plan = data?.plans.find((p) => p.groupname === groupname);
  const blocked = data ? whyDevicesBlock(data.devices) : "";
  // Pay online is checked like cash: no reference, the amount is the quote's total.
  const checkedMethod: RechargeMethod = online ? "cash" : method;
  const priced =
    total != null
      ? formError({
          groupname,
          amount: "0",
          method: checkedMethod,
          reference,
        }) ||
        (cart.pending
          ? "Wait for the price"
          : amountMatchesTotal(amount, total))
      : "";
  const unpriced =
    cartAddons(selection).length > 0
      ? "The add-ons could not be priced"
      : formError({ groupname, amount, method: checkedMethod, reference });
  const invalid =
    groupname && total != null
      ? priced
      : groupname && cart.pending
        ? "Wait for the price"
        : unpriced;
  const listed = plan ? listPriceWithGST(plan) : null;

  return (
    <Dialog open={subscriber !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Recharge</DialogTitle>
      {subscriber && (
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <Typography variant="body2">
            {subscriber.name || subscriber.username || subscriber.accountid}
          </Typography>
          {opts.error && (
            <Alert severity="error">
              {opts.error instanceof Error
                ? opts.error.message
                : String(opts.error)}
            </Alert>
          )}
          {data && (
            <Typography variant="caption" color="text.secondary">
              {data.devices.length === 1
                ? "1 device"
                : `${data.devices.length} devices`}
              {data.devices.length > 0 &&
                ": " +
                  data.devices
                    .map((d) =>
                      d.plan
                        ? `${d.username} (${d.plan}${d.expiry ? ` to ${d.expiry}` : ""})`
                        : d.username,
                    )
                    .join(", ")}
            </Typography>
          )}
          {blocked && <Alert severity="warning">{blocked}</Alert>}

          {link ? (
            <PayOnlinePanel ns={ns} accountId={accountId} started={link} />
          ) : done ? (
            <>
              <Alert severity="success">
                {done.replayed ? "Already recorded" : "Recharged"}:{" "}
                {saleReceipts(done).map((rc, i) => (
                  <span key={rc.receiptno}>
                    {i > 0 ? "; " : ""}
                    {receiptKindLabel(rc.kind).toLowerCase()}{" "}
                    <code>{rc.receiptno}</code>
                  </span>
                ))}
                {done.expiry
                  ? `, valid until ${new Date(done.expiry).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`
                  : ""}
                .
              </Alert>
              {send.isSuccess && (
                <Alert severity="success">
                  Receipt sent to {send.data.to}.
                </Alert>
              )}
              {send.error && (
                <Alert severity="error">
                  {send.error instanceof Error
                    ? send.error.message
                    : String(send.error)}
                </Alert>
              )}
            </>
          ) : (
            data &&
            !blocked && (
              <>
                <PlanPicker
                  plans={data.plans}
                  termPrices={termPrices}
                  value={groupname}
                  onChange={(g) => {
                    setGroupname(g);
                    setChangeOk(false);
                    setSelection({});
                    setSeededFor("");
                    setNotice("");
                  }}
                  why={whyNotRechargeable}
                  tag={pinWords}
                  disabled={recharge.isPending}
                />
                {data.plans.length === 0 && (
                  <Alert severity="info">
                    No plans in this namespace yet. Create one under Plans
                    first.
                  </Alert>
                )}
                {groupname && (
                  <CartAddons
                    mode="recharge"
                    speakGst={speakGst}
                    quote={quote}
                    loading={cart.pending}
                    error={cart.error}
                    selection={selection}
                    onChange={(sel) => {
                      setSelection(sel);
                      setNotice("");
                    }}
                    notice={notice}
                    disabled={recharge.isPending}
                    pinLabel={pinWords(
                      data.plans.find((x) => x.groupname === groupname),
                    )}
                  />
                )}
                {changeWarning && (
                  <Alert severity="warning">
                    {changeWarning}
                    <FormControlLabel
                      sx={{ display: "flex", mt: 0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={changeOk}
                          onChange={(e) => setChangeOk(e.target.checked)}
                        />
                      }
                      label="The subscriber asked to change the plan"
                    />
                  </Alert>
                )}
                {advanceWarning && (
                  <Alert severity="warning">
                    {advanceWarning}
                    <FormControlLabel
                      sx={{ display: "flex", mt: 0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={advanceOk}
                          onChange={(e) => setAdvanceOk(e.target.checked)}
                        />
                      }
                      label="The subscriber asked to pay in advance"
                    />
                  </Alert>
                )}
                {activeUntil && (
                  <>
                    <Typography variant="body2">
                      The current plan runs until {activeUntil} IST. When does
                      the new plan start?
                    </Typography>
                    <RadioGroup
                      value={start}
                      onChange={(e) =>
                        setStart(e.target.value as RechargeStart)
                      }
                    >
                      <FormControlLabel
                        value="after"
                        control={<Radio size="small" />}
                        label="After the current plan ends (no days lost)"
                      />
                      <FormControlLabel
                        value="now"
                        control={<Radio size="small" />}
                        label="Now (the unused value of the current plan is added as extra days)"
                      />
                    </RadioGroup>
                  </>
                )}
                <TextField
                  size="small"
                  label={
                    online ? "Amount to pay online (₹)" : "Amount collected (₹)"
                  }
                  disabled={online && total != null}
                  inputProps={{ inputMode: "decimal" }}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  helperText={
                    total != null
                      ? amountMatchesTotal(amount, total) ||
                        `The total ${rupees(total)}`
                      : listed != null
                        ? `List price ₹${listed}${speakGst ? " incl. GST" : ""}`
                        : "No list price for this plan; enter what you collected"
                  }
                  error={Boolean(
                    total != null && amountMatchesTotal(amount, total),
                  )}
                />
                <RadioGroup
                  row
                  value={method}
                  onChange={(e) => setMethod(e.target.value as RechargeMethod)}
                >
                  <FormControlLabel
                    value="cash"
                    control={<Radio size="small" />}
                    label="Cash"
                  />
                  <FormControlLabel
                    value="upi"
                    control={<Radio size="small" />}
                    label="UPI"
                  />
                  <FormControlLabel
                    value="online"
                    disabled={!canPayOnline}
                    control={<Radio size="small" />}
                    label="Pay online"
                  />
                </RadioGroup>
                {!canPayOnline && (
                  <PayOnlineNotice
                    options={
                      payOptions.data ?? { online: { available: false } }
                    }
                    readFailed={payOptions.isError}
                    why={PAY_ONLINE_OFF_NOTICE}
                  />
                )}
                {method === "upi" && (
                  <UpiPayQR
                    options={payOptions.data}
                    amount={Number(amount) > 0 ? Number(amount) : null}
                    note="Internet service"
                  />
                )}
                {online && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={notifySMS}
                        onChange={(e) => setNotifySMS(e.target.checked)}
                      />
                    }
                    label="Also text the payment link to the subscriber's mobile (sent by the payment gateway)"
                  />
                )}
                {payOnline.error && (
                  <Alert severity="error">
                    {payOnline.error instanceof Error
                      ? payOnline.error.message
                      : String(payOnline.error)}
                  </Alert>
                )}
                {method === "upi" && (
                  <TextField
                    size="small"
                    label="UPI reference (UTR)"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                )}
                {recharge.error && !isPriceChanged(recharge.error) && (
                  <Alert severity="error">
                    {recharge.error instanceof Error
                      ? recharge.error.message
                      : String(recharge.error)}
                  </Alert>
                )}
              </>
            )
          )}
        </DialogContent>
      )}
      <DialogActions>
        {link ? (
          <Button variant="contained" onClick={close}>
            {link.status === "Pending" ? "Close" : "Done"}
          </Button>
        ) : done ? (
          <>
            <Button
              disabled={send.isPending || send.isSuccess}
              onClick={() =>
                send.mutate(saleReceipts(done).map((rc) => rc.receiptno))
              }
            >
              {send.isPending ? "Sending…" : "Send to subscriber"}
            </Button>
            <Button variant="contained" onClick={close}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !data ||
                blocked !== "" ||
                invalid !== "" ||
                (advanceWarning !== "" && !advanceOk) ||
                (changeWarning !== "" && !changeOk) ||
                recharge.isPending ||
                payOnline.isPending
              }
              onClick={() =>
                online
                  ? payOnline.mutate()
                  : method === "cash"
                    ? setCashOpen(true)
                    : recharge.mutate(undefined)
              }
            >
              {online
                ? payOnline.isPending
                  ? "Creating link…"
                  : "Create payment link"
                : recharge.isPending
                  ? "Recharging…"
                  : "Recharge"}
            </Button>
          </>
        )}
      </DialogActions>
      {subscriber && (
        <CashCodeDialog
          open={cashOpen}
          identity={identity}
          ns={ns}
          account={accountId}
          amount={Number(amount)}
          saleRef={requestid}
          onCancel={() => setCashOpen(false)}
          onConfirm={(code) => {
            setCashOpen(false);
            recharge.mutate(code);
          }}
        />
      )}
    </Dialog>
  );
}
